import { FileType } from 'shared/entities/file';
import { isDataPreviewUrl } from 'shared/entities/file/preview';

const SVG_MIME_TYPE = 'image/svg+xml';
const VIDEO_THUMBNAIL_TIMEOUT_MS = 5000;
const MAX_THUMBNAIL_SIDE = 640;
const JPEG_QUALITY = 0.75;

const scaleToMaxSide = (
  width: number,
  height: number,
  maxSide: number
): { width: number; height: number } => {
  const longestSide = Math.max(width, height);

  if (longestSide <= maxSide || longestSide === 0) {
    return { width, height };
  }

  const scale = maxSide / longestSide;

  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale)
  };
};

const captureVideoFrame = (video: HTMLVideoElement): string | null => {
  const { width, height } = scaleToMaxSide(
    video.videoWidth,
    video.videoHeight,
    MAX_THUMBNAIL_SIDE
  );

  if (width === 0 || height === 0) {
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');

  if (!context) {
    return null;
  }

  context.drawImage(video, 0, 0, width, height);

  try {
    return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  } catch {
    return null;
  }
};

const readImageAsDataUrl = (file: File): Promise<string | null> =>
  new Promise((resolve) => {
    if (file.type === SVG_MIME_TYPE) {
      resolve(null);
      return;
    }

    const reader = new FileReader();

    reader.onload = (event) => {
      resolve((event.target?.result as string | null) ?? null);
    };

    reader.onerror = () => {
      resolve(null);
    };

    reader.readAsDataURL(file);
  });

const createVideoThumbnail = (file: File): Promise<string | null> =>
  new Promise((resolve) => {
    const video = document.createElement('video');
    const objectUrl = URL.createObjectURL(file);
    let settled = false;

    const finish = (result: string | null) => {
      if (settled) {
        return;
      }

      settled = true;
      clearTimeout(timeoutId);
      video.onloadeddata = null;
      video.onerror = null;
      video.src = '';
      URL.revokeObjectURL(objectUrl);
      resolve(result);
    };

    const timeoutId = window.setTimeout(() => {
      finish(null);
    }, VIDEO_THUMBNAIL_TIMEOUT_MS);

    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;

    video.onloadeddata = () => {
      finish(captureVideoFrame(video));
    };

    video.onerror = () => {
      finish(null);
    };

    video.src = objectUrl;
  });

export const createVideoThumbnailFromRemoteUrl = (
  remoteUrl: string
): Promise<string | null> =>
  new Promise((resolve) => {
    const video = document.createElement('video');
    let settled = false;

    const finish = (result: string | null) => {
      if (settled) {
        return;
      }

      settled = true;
      clearTimeout(timeoutId);
      video.onloadeddata = null;
      video.onerror = null;
      video.src = '';
      resolve(result);
    };

    const timeoutId = window.setTimeout(() => {
      finish(null);
    }, VIDEO_THUMBNAIL_TIMEOUT_MS);

    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';

    video.onloadeddata = () => {
      finish(captureVideoFrame(video));
    };

    video.onerror = () => {
      finish(null);
    };

    video.src = remoteUrl;
  });

export const buildRemotePreviewCandidates = (
  previewUrl: string | null,
  downloadUrl: string | null
): string[] => {
  if (!previewUrl) {
    return downloadUrl ? [downloadUrl] : [];
  }

  if (isDataPreviewUrl(previewUrl)) {
    return [previewUrl];
  }

  return [previewUrl, downloadUrl].filter(
    (value, index, array): value is string =>
      !!value && array.indexOf(value) === index
  );
};

export const loadFilePreview = async (
  file: File,
  fileType: FileType
): Promise<string | null> => {
  if (fileType === FileType.image) {
    return readImageAsDataUrl(file);
  }

  if (fileType === FileType.video) {
    return createVideoThumbnail(file);
  }

  return null;
};
